/**
 * BulkMessenger — reusable modal for FREE bulk SMS / WhatsApp
 * Uses native device deep-links (sms: / wa.me/) — zero cost.
 * Recipients pre-loaded from parent (Vyapar customers, ASST users, APTS parties, Safety subs).
 *
 * Props:
 *   open, onClose
 *   recipients: [{ id, name, mobile, ... }]   // must have mobile
 *   defaultTemplate: string (supports {name})
 *   label: string (dialog title)
 */
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { X, Copy, MessageSquare, Send, Users } from 'lucide-react';

const cleanPhone = (m) => String(m || '').replace(/\D/g, '');
const withCountry = (m) => {
  const c = cleanPhone(m);
  return c.length === 10 ? '91' + c : c;
};

export default function BulkMessenger({ open, onClose, recipients = [], defaultTemplate = '', label = 'Bulk Message' }) {
  const [selected, setSelected] = useState(() => new Set(recipients.map((r) => r.id)));
  const [template, setTemplate] = useState(defaultTemplate);
  const [busy, setBusy] = useState(false);

  const validRecipients = useMemo(() => recipients.filter((r) => cleanPhone(r.mobile).length >= 10), [recipients]);
  const picked = useMemo(() => validRecipients.filter((r) => selected.has(r.id)), [validRecipients, selected]);
  const numbers = useMemo(() => picked.map((r) => withCountry(r.mobile)), [picked]);

  if (!open) return null;

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const selectAll = () => setSelected(new Set(validRecipients.map((r) => r.id)));
  const selectNone = () => setSelected(new Set());

  const buildText = (r) => (template || '').replace(/\{name\}/gi, r.name || 'Grahak').replace(/\{mobile\}/gi, r.mobile || '');

  const sendSMS = () => {
    if (numbers.length === 0) return toast.error('Koi recipient select karein');
    if (!template.trim()) return toast.error('Message likhein');
    setBusy(true);
    // Native SMS app deep-link with comma-separated recipients (group SMS)
    const uniformText = buildText({ name: 'Grahak', mobile: '' });
    const url = `sms:${numbers.join(',')}?body=${encodeURIComponent(uniformText)}`;
    window.location.href = url;
    toast.success(`📱 ${numbers.length} recipients ke saath SMS app khul raha hai — Send tap karein`);
    setTimeout(() => setBusy(false), 800);
  };

  const sendWhatsApp = () => {
    if (picked.length === 0) return toast.error('Koi recipient select karein');
    if (!template.trim()) return toast.error('Message likhein');
    setBusy(true);
    let idx = 0;
    const openNext = () => {
      if (idx >= picked.length) { setBusy(false); toast.success(`✅ ${picked.length} WhatsApp windows opened`); return; }
      const r = picked[idx];
      const url = `https://wa.me/${withCountry(r.mobile)}?text=${encodeURIComponent(buildText(r))}`;
      window.open(url, '_blank');
      idx++;
      setTimeout(openNext, 800);
    };
    openNext();
  };

  const copyNumbers = () => {
    navigator.clipboard.writeText(numbers.join(', '));
    toast.success(`📋 ${numbers.length} numbers copied`);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div className="w-full max-w-lg rounded-2xl bg-slate-900 border border-emerald-500/30 p-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="bulk-messenger">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-2"><MessageSquare className="w-5 h-5 text-emerald-400" /><h3 className="text-lg font-extrabold">📢 {label}</h3></div>
          <button onClick={onClose} data-testid="bulk-close" className="p-1 rounded hover:bg-white/10"><X className="w-4 h-4" /></button>
        </div>

        {/* Recipients list */}
        <div className="mb-3">
          <div className="flex justify-between items-center mb-1.5">
            <div className="text-[11px] uppercase font-bold text-slate-400 flex items-center gap-1"><Users className="w-3 h-3" /> Recipients ({picked.length}/{validRecipients.length})</div>
            <div className="flex gap-1">
              <button onClick={selectAll} className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-bold">All</button>
              <button onClick={selectNone} className="text-[10px] px-2 py-0.5 rounded bg-white/10 text-slate-300 font-bold">None</button>
            </div>
          </div>
          <div className="max-h-40 overflow-y-auto rounded-lg border border-white/10 bg-slate-950/40 p-1.5 space-y-1">
            {validRecipients.length === 0 && <div className="text-center text-xs text-slate-500 py-3">Koi valid mobile number nahi mila</div>}
            {validRecipients.map((r) => (
              <label key={r.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-white/5 text-xs cursor-pointer">
                <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} data-testid={`bulk-pick-${r.id}`} className="accent-emerald-500" />
                <div className="flex-1 min-w-0"><b className="truncate">{r.name || '—'}</b> <span className="text-slate-400">· {r.mobile}</span></div>
              </label>
            ))}
          </div>
        </div>

        {/* Message template */}
        <label className="text-[11px] uppercase font-bold text-slate-400">Message (use <code className="text-emerald-300">{'{name}'}</code> for personalization)</label>
        <textarea rows={4} value={template} onChange={(e) => setTemplate(e.target.value)} data-testid="bulk-template" placeholder={'Namaste {name},\n\n...'} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" />
        <div className="text-[10px] text-slate-500 mt-0.5">SMS: personalization not applied (group SMS is one common text). WhatsApp: {'{name}'} replaced per contact.</div>

        {/* Send buttons */}
        <div className="grid grid-cols-2 gap-2 mt-3">
          <button onClick={sendSMS} disabled={busy || numbers.length === 0} data-testid="bulk-sms" className="py-2.5 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 text-white font-bold text-sm disabled:opacity-40 flex items-center justify-center gap-1"><Send className="w-4 h-4" /> SMS ({numbers.length})</button>
          <button onClick={sendWhatsApp} disabled={busy || picked.length === 0} data-testid="bulk-wa" className="py-2.5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-sm disabled:opacity-40 flex items-center justify-center gap-1">💚 WhatsApp ({picked.length})</button>
        </div>
        <button onClick={copyNumbers} disabled={numbers.length === 0} className="w-full mt-2 py-1.5 rounded-lg bg-white/5 text-slate-300 text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-40"><Copy className="w-3 h-3" /> Copy {numbers.length} numbers</button>
        <div className="mt-2 text-[10px] text-slate-500 leading-snug">
          <b>Free forever</b> — device pe SMS app khulega with pre-filled text. Aapke SMS plan pe depend karta hai (usually free with data pack). WhatsApp windows sequential khulti hain — har ek pe Send tap karein.
        </div>
      </div>
    </div>
  );
}
